import { useState, useEffect } from 'react';
import { useStore } from './lib/store';
import { themes } from './constants/themes';
import { getStardates } from './lib/stardate';

function App() {
  const { theme, setTheme, getCurrentTheme } = useStore();
  const currentTheme = getCurrentTheme();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [stardates, setStardates] = useState(getStardates());

  // Update stardates every second
  useEffect(() => {
    const interval = setInterval(() => {
      setStardates(getStardates());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: 'campaigns', label: 'CAMPAIGNS' },
    { id: 'sessions', label: 'SESSIONS' },
    { id: 'comms', label: 'COMMS' },
    { id: 'characters', label: 'CHARACTERS' },
    { id: 'npcs', label: 'NPCs' },
    { id: 'tables', label: 'TABLES' },
    { id: 'history', label: 'HISTORY' },
  ];

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: currentTheme.background, color: currentTheme.text }}
    >
      {/* LCARS Header */}
      <header 
        className="flex items-center justify-between px-8 py-4"
        style={{ 
          backgroundColor: currentTheme.primary,
          borderBottom: `4px solid ${currentTheme.secondary}`
        }}
      >
        <h1 className="text-3xl font-bold tracking-wider" style={{ color: currentTheme.background }}>
          STA GM DASHBOARD
        </h1>
        
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-end gap-1">
            <span className="text-sm font-mono" style={{ color: currentTheme.background }}>
              STIO: {stardates.stio}
            </span>
            <span className="text-sm font-mono" style={{ color: currentTheme.background }}>
              STA: {stardates.sta}
            </span>
          </div>
          
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            className="px-3 py-1 rounded font-mono text-sm"
            style={{ 
              backgroundColor: currentTheme.accent,
              color: currentTheme.background,
              border: 'none'
            }}
          >
            {Object.entries(themes).map(([key, t]) => (
              <option key={key} value={key}>{t.name}</option>
            ))}
          </select>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Left Sidebar Navigation */}
        <nav className="w-64 flex flex-col gap-2 p-4">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="py-12 px-6 text-left font-bold tracking-wider transition-all"
              style={{
                fontSize: '36px',
                backgroundColor: activeTab === tab.id 
                  ? currentTheme.primary 
                  : index % 3 === 0 
                    ? currentTheme.primary 
                    : index % 3 === 1 
                      ? currentTheme.secondary 
                      : currentTheme.accent,
                color: currentTheme.background,
                borderRadius: index % 2 === 0 ? '0 40px 40px 0' : '40px 0 0 40px',
                transform: activeTab === tab.id ? 'scale(1.05)' : 'scale(1)',
                boxShadow: activeTab === tab.id ? `0 0 20px ${currentTheme.primary}` : 'none',
              }}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Main Content Area */}
        <main className="flex-1 p-8">
          <div 
            className="rounded-lg p-6 min-h-full"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: `2px solid ${currentTheme.border}`
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: currentTheme.primary }}>
              {tabs.find(t => t.id === activeTab)?.label}
            </h2>
            
            <div className="text-lg">
              {activeTab === 'campaigns' && (
                <div>
                  <p className="mb-4">Campaign management interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Create and manage campaigns</li>
                    <li>Invite players</li>
                    <li>Assign characters</li>
                    <li>View campaign history</li>
                  </ul>
                </div>
              )}
              
              {activeTab === 'sessions' && (
                <div>
                  <p className="mb-4">Session control interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Start/end sessions</li>
                    <li>Monitor players in real-time</li>
                    <li>Manage momentum and threat</li>
                    <li>View recent events</li>
                  </ul>
                </div>
              )}
              
              {activeTab === 'comms' && (
                <div>
                  <p className="mb-4">Communications monitoring interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>View all messages across campaigns</li>
                    <li>Respond as NPCs</li>
                    <li>Respond as ship's computer</li>
                    <li>Manage channels</li>
                  </ul>
                </div>
              )}
              
              {activeTab === 'characters' && (
                <div>
                  <p className="mb-4">Character management interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>View all player characters</li>
                    <li>Award talents and modify stats</li>
                    <li>Grant equipment</li>
                    <li>View character history</li>
                  </ul>
                </div>
              )}
              
              {activeTab === 'npcs' && (
                <div>
                  <p className="mb-4">NPC management interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Create and track NPCs</li>
                    <li>Organize by faction/type</li>
                    <li>Monitor NPC status</li>
                    <li>Assign to encounters</li>
                  </ul>
                </div>
              )}
              
              {activeTab === 'tables' && (
                <div>
                  <p className="mb-4">Random tables interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Browse 18 random tables</li>
                    <li>Roll with animated results</li>
                    <li>Filter copyrighted content</li>
                    <li>View roll history</li>
                  </ul>
                </div>
              )}
              
              {activeTab === 'history' && (
                <div>
                  <p className="mb-4">Unified history interface coming soon...</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>View all session events</li>
                    <li>Filter by event type</li>
                    <li>Export as JSON</li>
                    <li>Clear completed sessions</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
